<template>
  <div
    class="dcb-sticky-button"
    :class="{ 'dcb-sticky-button--hidden': hidden }"
  >
    <LinkButton
      tag="button"
      type="button"
      primary
      is-wide
      size="medium"
      :tealium-name="tealiumName"
      :label="label"
      @click="scrollToTarget"
    />
  </div>
</template>

<script setup lang="ts">
import { LinkButton } from '@i22team/td-longtail-vue-components';

interface DcbStickyButtonProps {
  label: string;
  targetSelectors: string[];
  tealiumName?: string;
  scrollTargetId?: string;
}

const props = withDefaults(defineProps<DcbStickyButtonProps>(), {
  tealiumName: 'dcb.partner.sticky.scroll-to-register',
  scrollTargetId: 'dcb-mailform',
});

const hidden = ref(false);
let intersectionObserver: IntersectionObserver | null = null;

onMounted(() => {
  const targets = props.targetSelectors
    .map((s) => document.querySelector(s))
    .filter(Boolean) as Element[];

  if (!targets.length) return;

  const visible = new Set<Element>();
  intersectionObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) visible.add(e.target);
        else visible.delete(e.target);
      });
      hidden.value = visible.size > 0;
    },
    { threshold: 0.15 }
  );

  targets.forEach((el) => intersectionObserver!.observe(el));
});

onBeforeUnmount(() => {
  intersectionObserver?.disconnect();
  intersectionObserver = null;
});

const scrollToTarget = () => {
  document
    .getElementById(props.scrollTargetId)
    ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};
</script>

<style scoped lang="scss">
@use 'assets/base' as *;

.dcb-sticky-button {
  padding: 8px 16px;
  border-top: 1px solid var(--color-gray--200);
  background: var(--color-secondary);
  position: sticky;
  bottom: 0;
  z-index: 3;
  transition:
    transform 0.3s ease,
    opacity 0.3s ease;

  @include mq($mq-sm) {
    display: none;
  }

  &--hidden {
    opacity: 0;
    transform: translateY(100%);
    pointer-events: none;
  }
}
</style>
